import { useMemo, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { InsuranceType } from '@weave/schema-gen-ts/dist/schemas/insurance-verification/shared/enums.pb';
import {
  Plan,
  OnederfulPayerIDResponse,
} from '@weave/schema-gen-ts/dist/schemas/insurance-verification/v1/insurance_verification_adapter.pb';
import {
  ErrorDetails,
  DentalCoverageResponse,
  DentalCoverageRequest,
} from '@weave/schema-gen-ts/dist/schemas/insurance-verification/v2/insurance_verification_adapter.pb';
import { useInvalidateQueries } from '@frontend/api-insurance';
import { InsuranceDetailQueries } from '@frontend/api-insurance-detail';
import { useTranslation } from '@frontend/i18n';
import { useGetWeaveEnhancedInsuranceVerificationCustomizationFlagDetails } from '@frontend/insurance-verification';
import { useMutation } from '@frontend/react-query-helpers';
import {
  SchemaInsuranceVerificationAdapterServiceV1,
  SchemaInsuranceVerificationAdapterServiceV2,
} from '@frontend/schema';
import { useAppScopeStore } from '@frontend/scope';
import { titleCase } from '@frontend/string';
import { theme } from '@frontend/theme';
import {
  BannerNotification,
  Combobox,
  ContentLoader,
  DatePickerField,
  DropdownField,
  DropdownSearchField,
  FormRow,
  Heading,
  Text,
  TextField,
  Tray,
  useAlert,
} from '@frontend/design-system';
import { useGetLocationVerticalForPayerList } from '../../hooks/use-get-location-vertical-for-payer-list';
import { useGetVerifiedInsuranceDetailsShallowStore } from '../../hooks/use-get-verified-insurance-details-store';
import { useInsuranceForm } from '../../hooks/use-insurance-form';
import { useShouldEnableZipCode } from '../../hooks/use-should-enable-zipcode';
import { useInsuranceType } from '../../providers/insurance-type-provider';
import { usePersonInfoStore } from '../../providers/person-info-provider';
import { useInsurancePayerConnectionShallowStore } from '../../stores/use-insurance-payer-connection-store';
import { verifyInsuranceFormTrackingIds } from '../../tracking-ids';
import {
  MONTH_INFO,
  OTHER_SUBSCRIBER_SELECTION_VALUE,
  RELATIONSHIP_LIST,
  RelationshipEnum,
  VerificationError,
} from '../../utils/constants';
import { InsuranceDetails } from '../../utils/types';
import {
  getPayerIdAndPayerNameFromCombinedId,
  getRelationShipValue,
  removeNonAlphaNumericCharacters,
} from '../../utils/utils';
import { ErrorBanner } from '../error-banner';
import { PlanSearchInput } from './plan-search-input';
import { VerifyInsuranceSuccessSection } from './verify-insurance-success-section';

interface VerifyInsuranceSidePanelBodyProps {
  hasVerificationError: boolean;
  shouldShowTuuthFairyReport: boolean;
  onClose: () => void;
  onReportLearnMoreClick: () => void;
  /**
   * When the verify form is opened from the eligibility list view, this is the
   * appointment date currently displayed. Used to invalidate the matching
   * patient eligibility list query by exact key after a successful verify.
   */
  eligibilityListAppointmentDate?: string;
  /**
   * When the verify form is opened from the eligibility list view, this is the
   * insurance type (primary/secondary) currently displayed. The modal wraps
   * itself in its own InsuranceTypeProvider that always defaults to primary, so
   * this is threaded in explicitly to invalidate the correct list query key
   * after a successful verify.
   */
  eligibilityListInsuranceType?: InsuranceType;
}

export const VerifyInsuranceForm = ({
  hasVerificationError,
  shouldShowTuuthFairyReport,
  onClose,
  onReportLearnMoreClick,
  eligibilityListAppointmentDate,
  eligibilityListInsuranceType,
}: VerifyInsuranceSidePanelBodyProps) => {
  const { t } = useTranslation('insuranceDetails');
  const alert = useAlert();
  const { selectedLocationIds } = useAppScopeStore();

  const { personInfo, locationId } = usePersonInfoStore();
  const personId = personInfo?.PersonID ?? '';

  const isVerifyButtonClicked = useRef(false);
  const [errorDetails, setErrorDetails] = useState<ErrorDetails>();
  const [showSuccessPage, setShowSuccessPage] = useState(false);
  const [successPageInsuranceDetails, setSuccessPageInsuranceDetails] = useState<Partial<InsuranceDetails>>();

  const { refetchInsuranceDetails } = useGetVerifiedInsuranceDetailsShallowStore('refetchInsuranceDetails');

  const { isDental } = useGetLocationVerticalForPayerList(locationId || '');
  const { insuranceType } = useInsuranceType();
  const { isLoading, form, payerList, nationalProviderIdList, primSubscriberList, insuranceErrorDetails } =
    useInsuranceForm();
  const { setPayerConnectionPayerID, setPayerConnectionPayerWebsiteID } = useInsurancePayerConnectionShallowStore(
    'setPayerConnectionPayerID',
    'setPayerConnectionPayerWebsiteID'
  );
  const shouldEnableZIPCodeFields = useShouldEnableZipCode(form?.values?.payerId || '', locationId as string);
  const {
    invalidateGetProviderListQuery,
    invalidateGetInsuranceVerificationMetrics,
    invalidateGetInsuranceByPersonIdQuery,
    invalidateGetInsuranceVerificationDetailsPersonIdQuery,
    invalidateGetDentalInsuranceDetailsByPerson,
    invalidatePatientEligibilityListView,
  } = InsuranceDetailQueries.useInvalidateQueries();

  const invalidateEligibilityListViewIfActive = () => {
    const isOnEligibilityListView = window.location.pathname.includes('schedule/eligibility-list');
    if (isOnEligibilityListView && locationId && eligibilityListAppointmentDate) {
      // The modal renders inside its own InsuranceTypeProvider that always defaults
      // to primary, so prefer the insurance type actually displayed by the list
      // view to invalidate the correct query key (otherwise the secondary list
      // never refetches and the "Last checked" text goes stale).
      invalidatePatientEligibilityListView(
        locationId,
        eligibilityListAppointmentDate,
        eligibilityListInsuranceType ?? insuranceType
      );
    }
  };

  const { invalidateCheckInsuranceCountQuery } = useInvalidateQueries();

  const alphabeticallySortedPayerList = payerList.sort((a, b) => a.label.localeCompare(b.label));

  const { locationIdWiseCustomizationFlagDetails } = useGetWeaveEnhancedInsuranceVerificationCustomizationFlagDetails();
  const isEnhancedInsuranceVerificationEnabled = locationIdWiseCustomizationFlagDetails?.[locationId!] || false;
  const isMetlifePayerSelected = form?.values?.payerId?.toLowerCase().includes('metlife');
  const { payerId: selectedPayerId } = getPayerIdAndPayerNameFromCombinedId(form.values.payerId || '');

  const { data: payerConnectionDetails } = InsuranceDetailQueries.useGetPayerConnectionDetails(locationId || '', {
    enabled: !!locationId && !!selectedPayerId && isEnhancedInsuranceVerificationEnabled,
  });

  const navigateToPayerConnectionManagement = (payerId: string, payerWebsiteId: string) => {
    setPayerConnectionPayerID(payerId);
    setPayerConnectionPayerWebsiteID(payerWebsiteId);
    if (selectedLocationIds.length === 1) {
      window.location.hash = 'settings/insurance/payer-connection-management';
    } else {
      window.location.hash = `settings/insurance/payer-connection-management/${locationId}`;
    }
  };

  const selectedPayerConnection = useMemo(() => {
    if (!selectedPayerId || !isEnhancedInsuranceVerificationEnabled) return undefined;
    return payerConnectionDetails?.connections?.find(
      (connection) =>
        connection.payerId === selectedPayerId || connection.alternativePayerIds?.includes(selectedPayerId)
    );
  }, [payerConnectionDetails?.connections, selectedPayerId, isEnhancedInsuranceVerificationEnabled]);

  const shouldBlockVerifyForPayerConnection = useMemo(() => {
    if (!selectedPayerConnection || !isEnhancedInsuranceVerificationEnabled) return false;
    const isDisconnected = (selectedPayerConnection.status || '').toLowerCase() !== 'connected';
    return !selectedPayerConnection.isEdiSupported && isDisconnected;
  }, [selectedPayerConnection, isEnhancedInsuranceVerificationEnabled]);

  const webOnlyPayerMessage = useMemo(
    () => (
      <Text>
        {t(
          'This payer only supports website-based eligibility. Connect your payer account to verify patient eligibility.'
        )}
      </Text>
    ),
    [t]
  );

  const handleConnectPayerClick = () => {
    navigateToPayerConnectionManagement(
      selectedPayerConnection?.payerId || '',
      selectedPayerConnection?.websiteId || ''
    );
  };

  const handleDentalEnhancedInsuranceVerificationFlow = (res: DentalCoverageResponse) => {
    if (res) {
      onClose();
      alert.success(t('Insurance Details Submitted for Verification!'));
    } else {
      alert.error(t('Something went wrong. Please try again.'));
    }
  };

  const refetchInsuranceVerificationDetails = (personId: string, locationId: string, insuranceType: InsuranceType) => {
    if (refetchInsuranceDetails) {
      refetchInsuranceDetails?.();
    } else {
      invalidateGetInsuranceVerificationDetailsPersonIdQuery(personId, locationId, insuranceType);
    }
  };

  const handleDentalInsuranceVerificationFlow = (res: DentalCoverageResponse, payerId: string, payerName: string) => {
    if (res.coverage?.status) {
      const primarySubscriberValue = form.values.primarySubscriber;
      const subscriberId =
        !!primarySubscriberValue && primarySubscriberValue !== OTHER_SUBSCRIBER_SELECTION_VALUE
          ? primarySubscriberValue
          : '';

      setSuccessPageInsuranceDetails({
        payerId: payerId,
        payerName: payerName,
        groupName: form.values.groupName,
        groupNumber: form.values.groupNumber,
        firstName: form.values.patientFirstName,
        lastName: form.values.patientLastName,
        dateOfBirth: form.values.patientBirthDate,
        memberId: form.values.patientMemberId,
        nationalProviderId: form.values.nationalProviderId,
        providerTaxId: form.values.taxId,
        relationship: form.values.relationship as RelationshipEnum,
        subscriberId,
        subscriberFirstName: form.values.subscriberFirstName,
        subscriberLastName: form.values.subscriberLastName,
        subscriberDateOfBirth: form.values.subscriberBirthDate,
        subscriberMemberId: form.values.subscriberMemberId,
        coverageStatus: titleCase(res?.coverage?.status || ''),
        effectiveDate: res?.coverage?.effectiveDate || '',
      });
      setShowSuccessPage(true);
      alert.success(t('Insurance Verified!'));
    } else {
      // handle error case
      setErrorDetails(res.errorDetails);
    }
  };

  const { mutate: updateInsuranceDetails, isLoading: isUpdating } = useMutation({
    mutationFn: (data: DentalCoverageRequest) =>
      SchemaInsuranceVerificationAdapterServiceV2.GetDentalCoverageInfo(
        data,
        InsuranceDetailQueries.getLocationIdHeader(locationId)
      ),
    onSuccess: (res: DentalCoverageResponse) => {
      const { payerId, payerName } = getPayerIdAndPayerNameFromCombinedId(form.values.payerId || '');
      if (!isEnhancedInsuranceVerificationEnabled) {
        handleDentalInsuranceVerificationFlow(res, payerId, payerName);
      } else {
        handleDentalEnhancedInsuranceVerificationFlow(res);
      }
      const isNpiInProviderList =
        form.values.nationalProviderId && nationalProviderIdList.includes(form.values.nationalProviderId);

      if (isNpiInProviderList) {
        invalidateGetProviderListQuery(locationId!);
      }
      invalidateGetInsuranceVerificationMetrics(MONTH_INFO.startDate, MONTH_INFO.endDate, locationId!);
      invalidateGetInsuranceByPersonIdQuery(personId, locationId!);
      refetchInsuranceVerificationDetails(personId, locationId!, insuranceType);
      invalidateGetDentalInsuranceDetailsByPerson(personId);
      invalidateCheckInsuranceCountQuery();
      invalidateEligibilityListViewIfActive();
    },
    onError: () => {
      alert.error(t('Something went wrong while verifying insurance. Please try again.'));
    },
  });

  const handleSelectPlan = (plan: Plan) => {
    if (plan.cNam || plan.paID) {
      SchemaInsuranceVerificationAdapterServiceV1.GetOnederfulPayerID({
        electronicPayerId: plan.paID,
        payerName: plan.cNam,
      }).then((res: OnederfulPayerIDResponse) => {
        const isOnederfulPayerIdPresent =
          !!res.onederfulPayerId && res.onederfulPayerId !== VerificationError.WEAVE_PAYER_NOT_SUPPORTED;
        if (isOnederfulPayerIdPresent) {
          form.seedValues({
            payerId: res.onederfulPayerId ?? '',
            groupNumber: removeNonAlphaNumericCharacters(plan.gNum) ?? '',
            groupName: plan.gNam ?? '',
          });
        } else {
          form.getFieldProps('payerId').error = t('Payer Not supported');
          form.seedValues({
            groupNumber: removeNonAlphaNumericCharacters(plan.gNum) ?? '',
            groupName: plan.gNam ?? '',
          });
          alert.error(t('Payer Not Supported for selected plan!'));
        }
      });
    } else {
      form.seedValues({
        groupNumber: removeNonAlphaNumericCharacters(plan.gNum) ?? '',
        groupName: plan.gNam ?? '',
      });
    }
  };

  const handleDentalInsuranceCoverageInfo = (payerId: string, payerName: string, isDependent: boolean) => {
    updateInsuranceDetails({
      personId,
      payer: {
        id: payerId,
        name: payerName,
      },
      provider: {
        nationalProviderId: form.values.nationalProviderId,
        taxId: form.values.taxId,
      },
      relationship: getRelationShipValue(form.values.relationship as RelationshipEnum),
      patient: {
        isDependent,
        firstName: form.values.patientFirstName,
        lastName: form.values.patientLastName,
        dateOfBirth: form.values.patientBirthDate,
        memberId: form.values.patientMemberId,
        groupNumber: form.values.groupNumber,
        zipCode: form.values.patientZipCode,
      },
      ...(isDependent && {
        subscriber: {
          firstName: form.values.subscriberFirstName,
          lastName: form.values.subscriberLastName,
          dateOfBirth: form.values.subscriberBirthDate,
          memberId: form.values.subscriberMemberId,
          zipCode: form.values.subscriberZipCode,
        },
      }),
      insuranceType,
    });
  };

  const handleSaveClick = () => {
    form.validate();
    isVerifyButtonClicked.current = true;
    if (form.isComplete && isDental) {
      const isDependent = form.values.relationship !== RelationshipEnum.Self;
      const { payerId, payerName } = getPayerIdAndPayerNameFromCombinedId(form.values.payerId || '');

      handleDentalInsuranceCoverageInfo(payerId, payerName, isDependent);
    }
  };

  const errorDisplayOverride = isVerifyButtonClicked.current || hasVerificationError ? {} : { error: '' };

  const shouldRenderSubscriberInfo = useMemo(() => {
    const isRelationshipSelf =
      !!form.values.relationship &&
      (form.values.relationship === RelationshipEnum.Self || form.values.relationship?.toLowerCase() === 'self');
    const hasMultipleSubscribers = primSubscriberList.length > 1;
    const hasNoSubscribers = primSubscriberList.length === 0;

    // if the relationship is not self and we have multiple subscribers, we should render subscriber info only if the primary subscriber is selected
    if (hasMultipleSubscribers && !isRelationshipSelf) {
      return !!form.values.primarySubscriber;
    }

    // if the relationship is not self and we have no subscribers, we should render subscriber info to allow the user to add subscriber info manually without selecting the primary subscriber
    if (form?.values?.relationship && !isRelationshipSelf && hasNoSubscribers) {
      return true;
    }

    return false;
  }, [form.values.relationship, primSubscriberList.length, form.values.primarySubscriber]);

  if (showSuccessPage && successPageInsuranceDetails) {
    return (
      <VerifyInsuranceSuccessSection
        insuranceDetails={successPageInsuranceDetails}
        shouldShowTuuthFairyReport={shouldShowTuuthFairyReport}
        onClose={onClose}
        onReportLearnMoreClick={onReportLearnMoreClick}
      />
    );
  }

  return (
    <>
      <ContentLoader show={isLoading || isUpdating} />
      <ErrorBanner errorDetails={errorDetails || insuranceErrorDetails} customCss={{ marginTop: 0 }} />
      {shouldBlockVerifyForPayerConnection && (
        <BannerNotification status='error' message={webOnlyPayerMessage} css={{ marginBottom: theme.spacing(2) }} />
      )}
      {isDental && (
        <PlanSearchInput
          locationId={locationId ?? ''}
          sourceId={personInfo?.SourceID ?? ''}
          onPlanSelect={handleSelectPlan}
          data-trackingid={verifyInsuranceFormTrackingIds.planSearchInput}
        />
      )}
      <form {...form.formProps} autoComplete='off'>
        <Heading level={3} css={{ marginBottom: theme.spacing(3) }}>
          {t('Patient Information')}
        </Heading>
        <FormRow>
          <DropdownSearchField
            {...form.getFieldProps('payerId')}
            placeholder={t('Select Insurance Payer*')}
            label={t('Select Insurance Payer*')}
            searchFieldPlaceholder={t('Search Payer')}
            data-trackingid={verifyInsuranceFormTrackingIds.payerNameDropdown}
            {...errorDisplayOverride}
          >
            {alphabeticallySortedPayerList.map((payer) => (
              <DropdownSearchField.Option
                searchValue={payer.label}
                key={payer.value + payer.label}
                value={payer.uniqueCombinedId ?? payer.value}
              >
                {payer.label}
              </DropdownSearchField.Option>
            ))}
          </DropdownSearchField>
        </FormRow>

        <FormRow cols={[50, 50]}>
          <TextField
            data-trackingid={verifyInsuranceFormTrackingIds.groupNameInput}
            {...form.getFieldProps('groupName')}
            label={t('Group Name')}
          />
          <TextField
            data-trackingid={verifyInsuranceFormTrackingIds.groupNumberInput}
            {...form.getFieldProps('groupNumber')}
            label={t('Group Number')}
          />
        </FormRow>

        <FormRow>
          <TextField
            data-trackingid={verifyInsuranceFormTrackingIds.memberIdInput}
            {...form.getFieldProps('patientMemberId')}
            label={t('Select Member ID*')}
            {...errorDisplayOverride}
          />
        </FormRow>
        <FormRow>
          <TextField
            data-trackingid={verifyInsuranceFormTrackingIds.firstNameInput}
            {...form.getFieldProps('patientFirstName')}
            label={t('Enter First Name*')}
            {...errorDisplayOverride}
          />
        </FormRow>
        <FormRow>
          <TextField
            data-trackingid={verifyInsuranceFormTrackingIds.lastNameInput}
            {...form.getFieldProps('patientLastName')}
            label={t('Enter Last Name*')}
            {...errorDisplayOverride}
          />
        </FormRow>
        <FormRow>
          <DatePickerField
            data-trackingid={verifyInsuranceFormTrackingIds.dobInput}
            {...form.getFieldProps('patientBirthDate')}
            label={t('Date of Birth*')}
            css={datePickerStyle}
            {...errorDisplayOverride}
          />
        </FormRow>
        {shouldEnableZIPCodeFields && (
          <FormRow>
            <TextField
              data-trackingid={verifyInsuranceFormTrackingIds.zipCodeInput}
              {...form.getFieldProps('patientZipCode')}
              label={t('Patient Zip Code')}
              {...errorDisplayOverride}
            />
          </FormRow>
        )}
        <Heading level={3} css={{ marginBottom: theme.spacing(3) }}>
          {t('Provider Information')}
        </Heading>
        <FormRow cols={[50, 50]}>
          <Combobox
            data-trackingid={verifyInsuranceFormTrackingIds.nationalProviderIdInput}
            options={nationalProviderIdList}
            name='nationalProviderId'
            label={t('NPI Selector*')}
            fieldProps={{ ...form.getFieldProps('nationalProviderId'), ...errorDisplayOverride }}
          />
          <TextField
            data-trackingid={verifyInsuranceFormTrackingIds.taxIdInput}
            {...form.getFieldProps('taxId')}
            label={t('Tax ID*')}
            {...errorDisplayOverride}
          />
        </FormRow>
        <Heading level={3} css={{ marginBottom: theme.spacing(3) }}>
          {t('Relationship to Subscriber')}
        </Heading>
        <FormRow cols={[50, 50]}>
          <DropdownField
            data-trackingid={verifyInsuranceFormTrackingIds.relationshipToSubscriberDropdown}
            {...form.getFieldProps('relationship')}
            label={t('Relationship*')}
            placeholder={t('Select Relationship')}
            {...errorDisplayOverride}
          >
            {RELATIONSHIP_LIST.map(({ label, value }) => (
              <DropdownField.Option value={value} key={value}>
                {label}
              </DropdownField.Option>
            ))}
          </DropdownField>
          {!!primSubscriberList.length &&
            primSubscriberList.length > 1 &&
            form.values.relationship &&
            form.values.relationship !== RelationshipEnum.Self && (
              <div style={{ minWidth: 0 }}>
                <DropdownField
                  data-trackingid={verifyInsuranceFormTrackingIds.primarySubscriberDropdown}
                  {...form.getFieldProps('primarySubscriber')}
                  label={t('Subscriber')}
                  dropdownListStyle={css({ minWidth: 0 })}
                >
                  {primSubscriberList.map(({ label, value }) => (
                    <DropdownField.Option key={value} value={value}>
                      {label}
                    </DropdownField.Option>
                  ))}
                </DropdownField>
              </div>
            )}
        </FormRow>
        {shouldRenderSubscriberInfo && (
          <>
            <Heading level={3} css={{ marginBottom: theme.spacing(3) }}>
              {t('Subscriber Information')}
            </Heading>
            <FormRow>
              <TextField
                data-trackingid={verifyInsuranceFormTrackingIds.subscriberLastNameInput}
                {...form.getFieldProps('subscriberFirstName')}
                label={t('Subscriber First Name*')}
                {...errorDisplayOverride}
              />
            </FormRow>
            <FormRow>
              <TextField
                data-trackingid={verifyInsuranceFormTrackingIds.subscriberLastNameInput}
                {...form.getFieldProps('subscriberLastName')}
                label={t('Subscriber Last Name*')}
                {...errorDisplayOverride}
              />
            </FormRow>
            <FormRow cols={[50, 50]}>
              <DatePickerField
                data-trackingid={verifyInsuranceFormTrackingIds.subscriberDobInput}
                {...form.getFieldProps('subscriberBirthDate')}
                label={t('Date of Birth*')}
                {...errorDisplayOverride}
                css={datePickerStyle}
              />
              <TextField
                data-trackingid={verifyInsuranceFormTrackingIds.subscriberMemberIdInput}
                {...form.getFieldProps('subscriberMemberId')}
                label={t('Member ID*')}
                {...errorDisplayOverride}
              />
            </FormRow>
            {isEnhancedInsuranceVerificationEnabled && isMetlifePayerSelected && (
              <FormRow>
                <TextField
                  data-trackingid={verifyInsuranceFormTrackingIds.subscriberZipCodeInput}
                  {...form.getFieldProps('subscriberZipCode')}
                  label={t('Subscriber Zip Code')}
                  {...errorDisplayOverride}
                />
              </FormRow>
            )}
          </>
        )}
      </form>
      <Tray.Actions
        disabledPrimary={isLoading || isUpdating}
        onSecondaryClick={onClose}
        primaryLabel={shouldBlockVerifyForPayerConnection ? t('Connect Payer') : t('Verify')}
        onPrimaryClick={shouldBlockVerifyForPayerConnection ? handleConnectPayerClick : handleSaveClick}
        primaryTrackingId={
          shouldBlockVerifyForPayerConnection
            ? verifyInsuranceFormTrackingIds.webOnlyConnectPayerButton
            : verifyInsuranceFormTrackingIds.verifyButton
        }
        secondaryTrackingId={verifyInsuranceFormTrackingIds.cancelButton}
      />
    </>
  );
};

const datePickerStyle = css({
  maxWidth: '100%',
});
